import {FC, ReactNode, useEffect} from 'react';
import {Box, Skeleton} from '@mantine/core';

import {useDispatch, useSelector} from '~/store/hooks';
import {TNotification, TNotificationsResponse} from '~/services/hot-api/notifications';
import Loader from '~/common/Loader';
import {formatDate} from '~/utils/dates';
import {InfinityScroll} from '~/common/display';

import NotificationItem from '../NotificationItem/NotificationItem';
import s from './styles.module.scss';

interface NotificationsListProps {
  notifications: TNotificationsResponse;
  onNotificationOpen: (notification: TNotification) => void;
  onNotificationDelete: (notification: TNotification) => void;
  onNotificationAction: () => void;
}

const NotificationsList: FC<NotificationsListProps> = ({
  notifications,
  onNotificationOpen,
  onNotificationDelete,
  onNotificationAction,
}) => {
  const dispatch = useDispatch();
  const {items, total} = notifications;
  const {loading, loadingRemoveNotification} = useSelector((state) => ({
    loading: !!(
      state.loading.effects.profile.loadNotifications.loading ||
      state.loading.effects.profile.loadMoreNotifications.loading
    ),
    loadingRemoveNotification: !!state.loading.effects.profile.removeNotification.loading,
  }));

  // Дозагружаем элементы потому что в Инфините скроле
  // она не работает когда пустая лента
  useEffect(() => {
    if (!loading && total && !items.length) {
      dispatch.profile.loadMoreNotifications();
    }
  }, [loading, total, items.length]);

  const handleLoadMore = () => {
    if (total <= items.length) {
      return;
    }

    dispatch.profile.loadMoreNotifications();
  };

  if (loading && items.length === 0) {
    return (
      <div className={s['notifications-list']}>
        <Box className={s['notifications-list__skeletons']}>
          <Skeleton height={100} className={s.notifcation} />
          <Skeleton height={100} className={s.notifcation} />
          <Skeleton height={100} className={s.notifcation} />
        </Box>
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <div className={s['notifications-list']}>
        <Box>All caught up! No new notifications.</Box>
      </div>
    );
  }

  // Формируем список нотификаций с дополнительным хеадром с датой для каждого дня
  const listNodes = items.reduce<ReactNode[]>((acc, notification, index) => {
    const prevNotify = items[index - 1];

    if (prevNotify) {
      const dataPrev = formatDate(prevNotify.added, 'MMMM d');
      const current = formatDate(notification.added, 'MMMM d');

      if (current !== dataPrev) {
        acc.push(
          <div key={current} className={s['notification-list__day-title']}>
            {current}
          </div>
        );
      }
    }

    acc.push(
      <NotificationItem
        key={notification.id}
        notification={notification}
        onOpen={() => onNotificationOpen(notification)}
        onDelete={() => onNotificationDelete(notification)}
        onAction={onNotificationAction}
        isRead={Boolean(notification.readed || notification.viewed)}
      />
    );

    return acc;
  }, []);

  if (loading) {
    listNodes.push(<Loader mt={8} key="loader" variant="dots" />);
  }

  return (
    <InfinityScroll
      // два флага чтобы избежать рейса при удаление элементов с загрузкой
      loading={loading || loadingRemoveNotification}
      className={s['notifications-list']}
      onLoadMore={handleLoadMore}
    >
      {listNodes}
    </InfinityScroll>
  );
};

export default NotificationsList;
